import type { Context } from '@/overmind';

import type { RawData, SelectedGridElementLoadFlowDetails } from '../..';
import type { TacticResultBrowsingFilter } from '../../actions';
import type { CalculationState } from '../../createCalculationState';
import type { EvaluatedGridElementType } from '../gridElementsDetailsByCalculationType/GridElementType';
import {
  findGridElementLabel,
  findGridElementTitle
} from '../gridElementsDetailsByCalculationType/findGridElementLabel';
import type { GridElementLoadFlowDetails } from '../gridElementsDetailsByCalculationType/loadFlow/gridElementLoadFlowDetails.types';
import { getGridElementAttentionRequiringResultsCount } from './getGridElementAttentionRequiringResultsCount';
import { Features } from '../../createGridElementCalculationSetExecutionDetails';

export const selectedGridElement = (
  state: CalculationState,
  rootState: Context['state']
): CalculationState['selectedGridElement'] => {
  if (
    state.gridElementsDetailsByCalculationType.type === 'NO_EVALUATION' ||
    state.gridElementsAttentionStatuses.type === 'NOT_AVAILABLE' ||
    state.resultsBrowsingMode.current !== 'BROWSING_GRID_ELEMENT' ||
    state.isCalculationTacticResultUnavailable
  )
    return { type: 'NO_SELECTED' };

  const features =
    rootState.arcgis.featureGeometries.current === 'LOADED'
      ? rootState.arcgis.featureGeometries.value.flatMap(
          layer => layer.features
        )
      : [];

  const _features = new Features(features)

  const gridElementLoadFlow =
    state.gridElementsDetailsByCalculationType.gridElementsDetails.loadFlow.find(
      element =>
        element.id ===
        state.resultsBrowsingMode.matches('BROWSING_GRID_ELEMENT')
          ?.gridElementObjectId
    );
  const gridElementShortCircuit =
    state.gridElementsDetailsByCalculationType.gridElementsDetails.shortCircuit.find(
      element =>
        element.id ===
        state.resultsBrowsingMode.matches('BROWSING_GRID_ELEMENT')
          ?.gridElementObjectId
    );

  if (!gridElementLoadFlow && !gridElementShortCircuit) {
    if (state.functionalGridElements.type === 'NOT_AVAILABLE')
      return { type: 'NO_SELECTED' };

    const gridElementObjects = state.functionalGridElements.gridElements.filter(
      gridElement =>
        gridElement.objectId ===
        state.resultsBrowsingMode.matches('BROWSING_GRID_ELEMENT')
          ?.gridElementObjectId
    );
    let functionalGridElement;
    if (gridElementObjects.length > 1) {
      functionalGridElement = state.functionalGridElements.gridElements.find(
        gridElement =>
          gridElement.objectId ===
            state.resultsBrowsingMode.matches('BROWSING_GRID_ELEMENT')
              ?.gridElementObjectId &&
          gridElement.objectType === 'ExternalNetworkInjection'
      );
    } else {
      functionalGridElement = state.functionalGridElements.gridElements.find(
        gridElement =>
          gridElement.objectId ===
          state.resultsBrowsingMode.matches('BROWSING_GRID_ELEMENT')
            ?.gridElementObjectId
      );
    }
    if (!functionalGridElement) return { type: 'NO_SELECTED' };
    return {
      type: 'SELECTED_GRID_ELEMENT',
      gridElement: {
        attentionRequiringResultsCount: 0,
        loadflow: { type: 'NO_LOAD_FLOW_DETAILS' },
        shortCircuit: { type: 'NO_SHORT_CIRCUIT_DETAILS' },
        label: {
          name: findGridElementLabel(_features, functionalGridElement.objectId),
          title: findGridElementTitle(_features, functionalGridElement.objectId)
        },
        objectId: functionalGridElement.objectId,
        objectType: functionalGridElement.objectType
      }
    };
  }

  const objectId = gridElementLoadFlow?.id || gridElementShortCircuit?.id;

  if (!objectId) return { type: 'NO_SELECTED' };

  const objectType =
    gridElementLoadFlow?.objectType ||
    gridElementShortCircuit?.objectType ||
    'Unknown'; // TODO: a default type needs to be on element, not here!;
  return {
    type: 'SELECTED_GRID_ELEMENT',
    gridElement: {
      objectId,
      objectType,
      loadflow: selectLoadFlowDetails(
        gridElementLoadFlow,
        state.tacticResultBrowsingFilter,
        state.gridElementCalculationSetExecutionDetails
      ),
      shortCircuit: gridElementShortCircuit
        ? {
            type: 'SHORT_CIRCUIT_DETAILS',
            evaluatedResults: gridElementShortCircuit
          }
        : { type: 'NO_SHORT_CIRCUIT_DETAILS' },
      label: {
        name: `${
          objectType === 'Unknown' ? '' : objectType
        } ${findGridElementLabel(_features, objectId)}`,
        title: findGridElementTitle(_features, objectId)
      },
      attentionRequiringResultsCount:
        getGridElementAttentionRequiringResultsCount(
          objectId,
          state.gridElementsDetailsByCalculationType.gridElementsDetails,
          state.tacticResultBrowsingFilter,
          state.gridElementsAttentionStatuses.byCalculationType
        )
    }
  };
};
function selectLoadFlowDetails(
  gridElementLoadFlow: GridElementLoadFlowDetails | undefined,
  tacticResultBrowsingFilter: TacticResultBrowsingFilter,
  gridElementCalculationSetExecutionDetails: CalculationState['gridElementCalculationSetExecutionDetails']
): SelectedGridElementLoadFlowDetails<EvaluatedGridElementType> {
  return selectLoadFlowDetailsThreeLoadingScenario(
    gridElementLoadFlow,
    tacticResultBrowsingFilter,
    gridElementCalculationSetExecutionDetails
  );
}

function selectLoadFlowDetailsThreeLoadingScenario(
  gridElementLoadFlow: GridElementLoadFlowDetails | undefined,
  tacticResultBrowsingFilter: TacticResultBrowsingFilter,
  gridElementCalculationSetExecutionDetails: CalculationState['gridElementCalculationSetExecutionDetails']
): SelectedGridElementLoadFlowDetails<EvaluatedGridElementType> {
  if (!gridElementLoadFlow) return { type: 'NO_LOAD_FLOW_DETAILS' };
  if (gridElementCalculationSetExecutionDetails.type === 'NO_EVALUATION')
    return { type: 'NO_LOAD_FLOW_DETAILS' };

  const allScenarioDetailsByElementId =
    gridElementCalculationSetExecutionDetails.details.loadFlow.map(
      scenario => ({
        scenarioName: scenario.scenarioName,
        scenarioDetails: scenario.scenarioDetails.find(
          element => element.id === gridElementLoadFlow.id
        )
      })
    );

  if (allScenarioDetailsByElementId.some(scenario => !scenario.scenarioDetails))
    return { type: 'NO_LOAD_FLOW_DETAILS' };
  if (allScenarioDetailsByElementId.length === 1) {
    return {
      type: 'LOAD_FLOW_DETAILS',
      details: gridElementLoadFlow
    };
  }
  if (
    tacticResultBrowsingFilter.type === 'ALL' ||
    tacticResultBrowsingFilter.type === 'ALL_LOAD_FLOW' ||
    (tacticResultBrowsingFilter.type === 'SELECTED_LOAD_FLOW' &&
      tacticResultBrowsingFilter.scenarioIds.length === 3)
  ) {
    const allScenarioDetailsByElementId =
      gridElementCalculationSetExecutionDetails.details.loadFlow.map(
        scenario => ({
          scenarioName: scenario.scenarioName,
          scenarioDetails: scenario.scenarioDetails.find(
            element => element.id === gridElementLoadFlow.id
          )
        })
      );

    return {
      type: 'ALL_LOAD_FLOW_SCENARIO_DETAILS',
      details: allScenarioDetailsByElementId.map(scenario => ({
        scenarioName: scenario.scenarioName,
        scenarioDetails: scenario.scenarioDetails!
      }))
    };
  } else if (
    tacticResultBrowsingFilter.type === 'SELECTED_LOAD_FLOW' &&
    tacticResultBrowsingFilter.scenarioIds.length === 1
  ) {
    const selectedScenario =
      gridElementCalculationSetExecutionDetails.details.loadFlow.find(
        scenario =>
          scenario.scenarioId === tacticResultBrowsingFilter.scenarioIds[0]
      );

    if (!selectedScenario) return { type: 'NO_LOAD_FLOW_DETAILS' };

    const gridElementSelectedScenarioDetails =
      selectedScenario.scenarioDetails.find(
        detail => detail.id === gridElementLoadFlow.id
      );

    if (!gridElementSelectedScenarioDetails)
      return { type: 'NO_LOAD_FLOW_DETAILS' };

    return {
      type: 'LOAD_FLOW_DETAILS',
      details: {
        ...gridElementSelectedScenarioDetails,
        scenarioName: selectedScenario.scenarioName
      }
    };
  } else {
    return {
      type: 'LOAD_FLOW_DETAILS',
      details: gridElementLoadFlow
    };
  }
}
